import { Modal } from '@mantine/core';
import { IconBulb, IconInfoCircle, IconInfoSmall, type TablerIcon } from '@tabler/icons-react';
import { type CSSProperties, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { allMenuItems, findMenuTrail, MENU, type MenuItem, menuLeaves } from './menu.js';
import classes from './PageHelp.module.css';
import { tokenVar } from './tokens.js';

/**
 * Sayfa bilgi penceresinin içeriği (ADR 0053 §3): `help.<menuId>.summary` (1–2 cümle),
 * `help.<menuId>.steps` (3–5 adım) ve isteğe bağlı `help.<menuId>.tip`.
 */
export interface PageHelpContent {
  summary: string;
  steps: string[];
  tip?: string;
}

/** Yolun bilgi anahtarı: menüdeki izin son öğesi; menüde yoksa `null` (ADR 0053 §2). */
export function pageHelpKey(path: string, menu: readonly MenuItem[] = MENU): string | null {
  return findMenuTrail(path, menu).at(-1)?.id ?? null;
}

type Translate = (key: string, options?: { returnObjects?: boolean }) => unknown;

/** İçerik i18n'den okunur; anahtarın özeti yoksa `null` (düğme çizilmez). */
export function readPageHelp(
  t: unknown,
  exists: (key: string) => boolean,
  key: string,
): PageHelpContent | null {
  const translate = t as Translate;
  if (!exists(`help.${key}.summary`)) return null;
  const steps = translate(`help.${key}.steps`, { returnObjects: true });
  const content: PageHelpContent = {
    summary: String(translate(`help.${key}.summary`)),
    steps: Array.isArray(steps) ? steps.map(String) : [],
  };
  if (exists(`help.${key}.tip`)) content.tip = String(translate(`help.${key}.tip`));
  return content;
}

/**
 * İçerik kaydının denetimi (ADR 0053 §4): her menü yaprağının iki dilde özeti, 3–5 adımı var;
 * adım sayısı ve ipucunun varlığı iki dilde aynı. Boş dizi = sorun yok. Uygulamaların testleri kendi
 * menüleri için çağırır; yeni ekran içeriksiz eklenirse test kırılır.
 */
export function pageHelpProblems(
  menu: readonly MenuItem[],
  resources: { en: object; tr: object },
): string[] {
  const problems: string[] = [];
  const read = (language: 'en' | 'tr', id: string) =>
    (resources[language] as { help?: Record<string, Partial<PageHelpContent>> }).help?.[id];
  for (const { item } of menuLeaves(menu)) {
    const found: Partial<Record<'en' | 'tr', Partial<PageHelpContent>>> = {};
    for (const language of ['en', 'tr'] as const) {
      const where = `help.${item.id} (${language})`;
      const content = read(language, item.id);
      if (!content?.summary) {
        problems.push(`${where}: summary yok`);
        continue;
      }
      found[language] = content;
      const steps = Array.isArray(content.steps) ? content.steps : [];
      if (steps.length < 3 || steps.length > 5) {
        problems.push(`${where}: 3–5 adım olmalı (${steps.length})`);
      }
      if (steps.some((step) => !step)) problems.push(`${where}: boş adım`);
      if (content.tip !== undefined && !content.tip) problems.push(`${where}: boş ipucu`);
    }
    const { en, tr } = found;
    if (!en || !tr) continue;
    if ((en.steps?.length ?? 0) !== (tr.steps?.length ?? 0)) {
      problems.push(`help.${item.id}: adım sayısı iki dilde farklı`);
    }
    if ((en.tip === undefined) !== (tr.tip === undefined)) {
      problems.push(`help.${item.id}: ipucu yalnız bir dilde`);
    }
  }
  return problems;
}

/** Anahtarın menüdeki ikonu: öğenin ya da ana grubunun ikonu. */
function iconOf(key: string, menu: readonly MenuItem[]): TablerIcon {
  const item = allMenuItems(menu).find((candidate) => candidate.id === key);
  if (!item) return IconInfoCircle;
  return findMenuTrail(item.path, menu).find((step) => step.icon)?.icon ?? IconInfoCircle;
}

/**
 * Pencerenin üstündeki küçük derinlikli sahne (ADR 0053 §5): katmanlı sayfalar, üstte sayfanın
 * ikonunu taşıyan parlak kart, yavaş dönen yörünge. Saf CSS 3B; "hareketi azalt"ta durur.
 */
export function PageHelpScene({ icon: Icon }: { icon: TablerIcon }) {
  return (
    <div className={classes.scene} data-help-scene aria-hidden>
      <div className={classes.stage}>
        <span className={classes.orbit}>
          <span className={classes.spark} />
        </span>
        <span className={classes.stack}>
          {[0, 1, 2].map((layer) => (
            <span
              key={layer}
              className={classes.sheet}
              style={{ '--layer': layer } as CSSProperties}
            />
          ))}
        </span>
        <span className={classes.tile}>
          <Icon size={30} stroke={1.6} />
          <span className={classes.shine} />
        </span>
      </div>
    </div>
  );
}

/** Pencerenin gövdesi: açıklama, numaralı adımlar ve ipucu (statik; birim testi bunu çizer). */
export function PageHelpBody({ content }: { content: PageHelpContent }) {
  const { t } = useTranslation();
  return (
    <div className={classes.body}>
      <p className={classes.summary}>{content.summary}</p>
      <h3 className={classes.heading}>{t('pageHelp.howTo')}</h3>
      <ol className={classes.steps}>
        {content.steps.map((step, index) => (
          <li key={step} className={classes.step} style={{ '--i': index } as CSSProperties}>
            {step}
          </li>
        ))}
      </ol>
      {content.tip ? (
        <div className={classes.tip} data-help-tip>
          <IconBulb size={18} stroke={1.7} className={classes.tipIcon} aria-hidden />
          <p>
            <strong>{t('pageHelp.tip')}</strong> {content.tip}
          </p>
        </div>
      ) : null}
    </div>
  );
}

export interface PageHelpButtonProps {
  /** Bilgi anahtarı (menü kimliği). */
  helpKey: string;
  /** İçerik (`readPageHelp`). */
  content: PageHelpContent;
  /** Pencerenin başlığı: sayfanın adı. */
  title: string;
  /** İkonun arandığı menü (kabuğun menüsü). */
  menu?: readonly MenuItem[] | undefined;
}

/**
 * Sayfa başlığının yanındaki "i" düğmesi ve açtığı pencere (ADR 0053). İçerik yoksa başlık düğmeyi
 * çizmez. Escape ve dış tıklama kapatır; odak düğmeye döner.
 */
export function PageHelpButton({ helpKey, content, title, menu = MENU }: PageHelpButtonProps) {
  const { t } = useTranslation();
  const [opened, setOpened] = useState(false);
  const Icon = iconOf(helpKey, menu);

  return (
    <>
      <button
        type="button"
        className={classes.button}
        aria-label={t('pageHelp.button')}
        aria-haspopup="dialog"
        aria-expanded={opened}
        title={t('pageHelp.button')}
        onClick={() => setOpened(true)}
        data-page-help={helpKey}
      >
        <IconInfoSmall size={20} stroke={2} aria-hidden />
      </button>
      <Modal.Root
        opened={opened}
        onClose={() => setOpened(false)}
        centered
        size={460}
        radius="lg"
        classNames={{ root: classes.root, inner: classes.inner, content: classes.content }}
        // Temanın pencere başlığı lacivert (satır içi stil); burada koyu sahnenin üstünde beyaz
        styles={{ title: TITLE_STYLE }}
        transitionProps={{ transition: RISE, duration: 260, timingFunction: EASE }}
      >
        <Modal.Overlay backgroundOpacity={0.45} blur={3} />
        <Modal.Content data-page-help-dialog>
          <div className={classes.hero}>
            <div className={classes.heroText}>
              <span className={classes.eyebrow}>{t('pageHelp.eyebrow')}</span>
              <Modal.Title className={classes.title}>{title}</Modal.Title>
            </div>
            <PageHelpScene icon={Icon} />
            <Modal.CloseButton className={classes.close} aria-label={t('pageHelp.close')} />
          </div>
          <Modal.Body className={classes.modalBody}>
            <PageHelpBody content={content} />
          </Modal.Body>
        </Modal.Content>
      </Modal.Root>
    </>
  );
}

const TITLE_STYLE = {
  color: tokenVar('surface'),
  fontSize: '1.3125rem',
  fontWeight: 700,
  lineHeight: 1.25,
};

/** Açılış: aşağıdan hafifçe yükselip büyür; kapanışta tersine. */
const RISE = {
  in: { opacity: 1, transform: 'translateY(0) scale(1)' },
  out: { opacity: 0, transform: 'translateY(24px) scale(0.96)' },
  transitionProperty: 'transform, opacity',
};
const EASE = 'cubic-bezier(0.2, 0.8, 0.2, 1)';
